import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { StatusPill, Spinner } from "@/components/ui-bits";
import { PlatformAlerts } from "@/components/PlatformAlerts";
import OnboardingWizard, { OpenGuideButton } from "@/components/OnboardingWizard";
import { Bell, ChevronRight, LogOut, ChevronLeft, ChevronRight as ChevronR, Clock, Pause, X, Users as UsersIcon, Ship, UserCircle, FileText } from "lucide-react";
import { useLang } from "@/i18n/LangContext";

const MONTHS_ES = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
                   "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
const MONTHS_EN = ["January", "February", "March", "April", "May", "June",
                   "July", "August", "September", "October", "November", "December"];
const WEEKDAYS_ES = ["L", "M", "X", "J", "V", "S", "D"];
const WEEKDAYS_EN = ["M", "T", "W", "T", "F", "S", "S"];

const fmtHours = (h) => {
  if (!h || h <= 0) return "0h";
  const hh = Math.floor(h);
  const mm = Math.round((h - hh) * 60);
  if (hh === 0) return `${mm}m`;
  if (mm === 0) return `${hh}h`;
  return `${hh}h ${mm}m`;
};

export default function Inicio() {
  const { user, logout } = useAuth();
  const { t, lang } = useLang();
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [todayItems, setTodayItems] = useState([]);
  const [monthJobs, setMonthJobs] = useState([]);
  const [embs, setEmbs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState(false);
  const [sub, setSub] = useState(null);
  const [cursor, setCursor] = useState(() => {
    const d = new Date(); d.setDate(1); return d;
  });
  const [report, setReport] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);

  const currentMonth = `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, "0")}`;

  // Si falla cualquiera de las peticiones se muestra un error con "Reintentar": antes el fallo era
  // silencioso y las tarjetas se quedaban en 0 / "sin trabajos" aunque sí hubiera trabajos.
  const loadDashboard = async () => {
    setLoading(true);
    setLoadErr(false);
    try {
        const today = localISO(new Date());
        const [s, t, ec, pa, e, subRes] = await Promise.all([
          api.get("/dashboard/stats"),
          api.get("/mantenimientos", { params: { date_from: today, date_to: today } }),
          api.get("/mantenimientos", { params: { status: "en_curso" } }),
          api.get("/mantenimientos", { params: { status: "pausado" } }),
          api.get("/embarcaciones"),
          api.get("/subscription/status").catch(() => ({ data: null })),
        ]);
        setStats(s.data);
        const merged = {};
        [...t.data, ...ec.data, ...pa.data].forEach((m) => { merged[m.id] = m; });
        setTodayItems(Object.values(merged));
        setEmbs(e.data);
        setSub(subRes.data);
    } catch (ex) {
      // eslint-disable-next-line no-console
      console.error("Inicio load failed:", ex);
      setLoadErr(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // El informe de horas (calendario) no es crítico: se pide cuando las tarjetas ya están pintadas
  useEffect(() => {
    if (loading) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/informes/horas-empleado", { params: { month: currentMonth } });
        if (!cancelled) setReport(r.data);
      } catch {
        if (!cancelled) setReport(null);
      }
    })();
    return () => { cancelled = true; };
  }, [currentMonth, loading]);

  // Trabajos del mes (todos los estados) para marcar el calendario
  useEffect(() => {
    const y = cursor.getFullYear(), m = cursor.getMonth();
    api.get("/mantenimientos", { params: { date_from: localISO(new Date(y, m, 1)), date_to: localISO(new Date(y, m + 1, 0)) } })
      .then((r) => setMonthJobs(r.data)).catch(() => setMonthJobs([]));
  }, [cursor]);

  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 12) return t("greet.morning");
    if (h < 20) return t("greet.afternoon");
    return t("greet.evening");
  })();

  const embMap = useMemo(() => Object.fromEntries(embs.map((x) => [x.id, x])), [embs]);

  // En curso del dashboard: debe salir del MISMO array que pinta "Trabajos de hoy",
  // nunca de una llamada aparte, o pueden desincronizarse si algo cambia entre peticiones.
  const enCursoCount = useMemo(
    () => todayItems.filter((x) => x.status === "en_curso" || x.status === "pausado").length,
    [todayItems]
  );

  // Hoy = trabajos con fecha de hoy (cualquier estado) + todos los que están en curso/pausados
  const visibleItems = useMemo(() => {
    const rank = { en_curso: 0, pausado: 1, retraso: 2, programado: 3, finalizado: 4 };
    return [...todayItems]
      .sort((a, b) => {
        const ra = rank[a.status] ?? 99;
        const rb = rank[b.status] ?? 99;
        if (ra !== rb) return ra - rb;
        return (b.start_at || "").localeCompare(a.start_at || "");
      });
  }, [todayItems]);

  const daysMap = useMemo(() => {
    const map = {};
    (report?.days || []).forEach((d) => { map[d.date] = d; });
    return map;
  }, [report]);

  const jobsByDay = useMemo(() => {
    const map = {};
    monthJobs.forEach((j) => { const k = (j.start_at || "").slice(0, 10); (map[k] = map[k] || []).push(j); });
    return map;
  }, [monthJobs]);

  // Build calendar grid for cursor month (Mon-first)
  const calendar = useMemo(() => {
    const y = cursor.getFullYear(), m = cursor.getMonth();
    const first = new Date(y, m, 1);
    const startIdx = (first.getDay() + 6) % 7; // lunes=0
    const totalDays = new Date(y, m + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < startIdx; i++) cells.push(null);
    for (let d = 1; d <= totalDays; d++) {
      const iso = `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
      cells.push({ day: d, iso, info: daysMap[iso], jobs: jobsByDay[iso] || [] });
    }
    return cells;
  }, [cursor, daysMap, jobsByDay]);

  const prevMonth = () => { const d = new Date(cursor); d.setMonth(d.getMonth() - 1); setCursor(d); };
  const nextMonth = () => { const d = new Date(cursor); d.setMonth(d.getMonth() + 1); setCursor(d); };

  const dayDetail = selectedDay ? (daysMap[selectedDay] || { date: selectedDay, items: [] }) : null;
  const dayJobs = selectedDay ? (jobsByDay[selectedDay] || []) : [];

  return (
    <div className="px-5 pt-8 pb-8 sns-fade-up" data-testid="page-inicio">
      <div className="flex items-start justify-between mb-6">
        <div>
          <p className="text-slate-500 text-sm">{greeting},</p>
          <h1 className="sns-title text-3xl">{user?.name?.split(" ")[0] || t("greet.hello")} 👋</h1>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => nav("/trabajos")}
            className="w-11 h-11 rounded-full bg-white border border-[var(--border)] flex items-center justify-center relative hover:border-[var(--coral)] transition-colors"
            data-testid="btn-notifications"
            aria-label={t("nav.notifications") || "Notificaciones"}
          >
            <Bell size={18} />
            {(stats?.programados_proximos ?? stats?.programados) > 0 && (
              <span className="absolute -top-1 -right-1 bg-[var(--coral)] text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center">
                {stats.programados_proximos ?? stats.programados}
              </span>
            )}
          </button>
          <button
            onClick={() => { logout(); nav("/login"); }}
            className="w-11 h-11 rounded-full bg-white border border-[var(--border)] flex items-center justify-center text-slate-600 hover:text-[var(--coral)] hover:border-[var(--coral)] transition-colors"
            aria-label={t("nav.logout")}
            data-testid="btn-logout-inicio"
          >
            <LogOut size={18} />
          </button>
        </div>
      </div>

      {loading ? <Spinner /> : loadErr ? (
        <div className="bg-red-50 border border-red-200 rounded-2xl p-4 text-sm text-red-800 flex flex-col gap-3" data-testid="inicio-error">
          <div className="font-semibold">⚠️ No se pudieron cargar los datos del inicio</div>
          <button
            onClick={loadDashboard}
            className="self-start bg-red-600 text-white px-4 py-2 rounded-lg font-semibold text-sm active:scale-95"
            data-testid="btn-reload-inicio"
          >
            Reintentar
          </button>
        </div>
      ) : (
        <>
          <OnboardingWizard />
          <PlatformAlerts />
          <OpenGuideButton />

          {/* Banner de trial: días restantes con urgencia visual */}
          {sub?.trial_active && (
            <TrialBanner sub={sub} nav={nav} t={t} />
          )}

          {/* Banner: trial expirado (plan=free pero antes tuvo trial) */}
          {sub && !sub.trial_active && sub.plan === "free" && sub.status !== "free" && user?.role === "admin" && (
            <div className="sns-card p-4 mb-4 bg-red-50 border-2 border-red-300" data-testid="trial-expired-banner">
              <div className="flex items-start gap-3">
                <div className="text-2xl">🔒</div>
                <div className="flex-1">
                  <h4 className="font-bold text-red-900 mb-1">{t("plan.trial.ends_today")}</h4>
                  <p className="text-sm text-red-800 mb-3">Puedes seguir usando lo creado, pero para añadir más necesitas Pro.</p>
                  <button onClick={() => nav("/plan")} className="sns-btn-primary text-sm px-4 py-2" data-testid="btn-upgrade-expired">
                    {t("plan.trial.upgrade_cta")}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Stats grid */}
          <div className="grid grid-cols-2 gap-3 mb-6">
            <StatCard label={t("inicio.stat.today")} value={visibleItems.length} color="#FF6B3D" bg="#FFE6D7"
                      onClick={() => nav("/trabajos")} testid="stat-hoy" />
            <StatCard label={t("inicio.stat.scheduled")} value={stats?.programados || 0} color="#2D62B7" bg="#E8F1FF"
                      onClick={() => nav("/trabajos?filter=programado")} testid="stat-programados" />
            <StatCard label={t("inicio.stat.in_progress")} value={enCursoCount} color="#B45309" bg="#FEF3C7"
                      onClick={() => nav("/trabajos?filter=en_curso")} testid="stat-en-curso" />
            <StatCard label={t("inicio.stat.completed")} value={stats?.finalizados || 0} color="#116A34" bg="#D5F5E0"
                      onClick={() => nav("/trabajos?filter=finalizado")} testid="stat-finalizados" />
          </div>

          {/* Gestión rápida: solo admin, visible en móvil (sidebar la cubre en desktop) */}
          {user?.role === "admin" && (
            <div className="lg:hidden mb-6" data-testid="admin-quick-access">
              <h2 className="sns-title text-xl mb-3">{t("inicio.quick_actions") || "Gestión rápida"}</h2>
              <div className="grid grid-cols-4 gap-3">
                <QuickActionCard Icon={UsersIcon} label={t("nav.clientes") || "Clientes"} color="#2D62B7" bg="#E8F1FF"
                                 onClick={() => nav("/gestion/clientes")} testid="quick-clientes" />
                <QuickActionCard Icon={Ship} label={t("nav.embarcaciones") || "Propiedades"} color="#0369A1" bg="#E0F2FE"
                                 onClick={() => nav("/gestion/embarcaciones")} testid="quick-propiedades" />
                <QuickActionCard Icon={UserCircle} label={t("nav.usuarios") || "Empleados"} color="#7C3AED" bg="#F3E8FF"
                                 onClick={() => nav("/gestion/usuarios")} testid="quick-empleados" />
                <QuickActionCard Icon={FileText} label={t("nav.albaranes") || "Albaranes"} color="#116A34" bg="#D5F5E0"
                                 onClick={() => nav("/gestion/albaranes")} testid="quick-albaranes" />
              </div>
            </div>
          )}

          {/* Trabajos del día: en curso + completados (sin programados) */}
          <div className="flex items-center justify-between mb-3">
            <h2 className="sns-title text-xl">{t("inicio.today_jobs")}</h2>
            <Link to="/trabajos" className="text-sm text-[var(--coral)] font-semibold flex items-center gap-1" data-testid="link-ver-todos">
              {t("inicio.see_all")} <ChevronRight size={14} />
            </Link>
          </div>

          {visibleItems.length === 0 ? (
            <div className="sns-card p-6 text-center text-slate-500 text-sm" data-testid="inicio-no-jobs">
              {t("inicio.no_jobs")}
            </div>
          ) : (
            <div className="space-y-3">
              {visibleItems.map((m) => (
                <MiniJobCard key={m.id} m={m} emb={embMap[m.embarcacion_id]} />
              ))}
            </div>
          )}

          {/* Calendario de horas trabajadas */}
          <div className="sns-card p-5 mt-6" data-testid="calendar-horas">
            <div className="flex items-center justify-between mb-4">
              <button onClick={prevMonth} className="w-9 h-9 rounded-full hover:bg-slate-100 flex items-center justify-center" data-testid="btn-prev-month">
                <ChevronLeft size={18} />
              </button>
              <div className="text-center">
                <div className="sns-title text-lg">{(lang === "en" ? MONTHS_EN : MONTHS_ES)[cursor.getMonth()]} {cursor.getFullYear()}</div>
                <div className="text-xs text-slate-500">{t("inicio.calendar.my_hours")}</div>
              </div>
              <button onClick={nextMonth} className="w-9 h-9 rounded-full hover:bg-slate-100 flex items-center justify-center" data-testid="btn-next-month">
                <ChevronR size={18} />
              </button>
            </div>

            <div className="grid grid-cols-7 gap-1 mb-2 text-center text-xs font-bold text-slate-400">
              {(lang === "en" ? WEEKDAYS_EN : WEEKDAYS_ES).map((d, i) => <div key={i}>{d}</div>)}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {calendar.map((c, i) => (
                <CalendarCell key={i} cell={c} onSelect={() => (c?.info || c?.jobs.length) && setSelectedDay(c.iso)} />
              ))}
            </div>

            <div className="mt-4 pt-4 border-t border-[var(--border)] text-center">
              <div className="text-xs text-slate-500 mb-1">{user?.role === "admin" ? t("inicio.calendar.total_team", "Total horas del equipo este mes") : t("inicio.calendar.total_month")}</div>
              <div className="sns-title text-3xl text-[var(--coral)]" data-testid="total-hours">{fmtHours(report?.total_hours || 0)}</div>
            </div>
          </div>
        </>
      )}

      {/* Day detail modal */}
      {dayDetail && (
        <DayDetailModal
          day={dayDetail}
          jobs={dayJobs}
          embMap={embMap}
          onClose={() => setSelectedDay(null)}
          t={t}
          lang={lang}
        />
      )}
    </div>
  );
}

const TrialBanner = ({ sub, nav, t }) => {
  const days = sub?.trial_days_left || 0;
  const isUrgent = days <= 2;
  const isWarning = days > 2 && days <= 7;
  const bg = isUrgent ? "bg-red-50 border-red-300" : isWarning ? "bg-amber-50 border-amber-300" : "bg-blue-50 border-blue-300";
  const emoji = isUrgent ? "🚨" : isWarning ? "⏰" : "✨";
  const textColor = isUrgent ? "text-red-900" : isWarning ? "text-amber-900" : "text-blue-900";
  const label = days === 0
    ? t("plan.trial.ends_today")
    : (days === 1 ? t("plan.trial.days_left_one") : t("plan.trial.days_left_many")).replace("{n}", days);

  return (
    <button
      onClick={() => nav("/plan")}
      className={`w-full sns-card p-4 mb-4 border-2 ${bg} text-left flex items-center gap-3 hover:shadow-md transition-shadow`}
      data-testid="trial-banner"
    >
      <div className="text-2xl">{emoji}</div>
      <div className="flex-1">
        <p className={`font-bold ${textColor}`}>{label}</p>
        <p className="text-xs text-slate-600 mt-0.5">{t("plan.trial.upgrade_cta")} →</p>
      </div>
    </button>
  );
};


const StatCard = ({ label, value, color, bg, onClick, testid }) => (
  <button
    type="button"
    onClick={onClick}
    className="sns-card p-5 text-left hover:shadow-md hover:-translate-y-0.5 active:scale-[0.98] transition-all cursor-pointer w-full"
    data-testid={testid}
  >
    <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3" style={{ background: bg, color }}>
      <span className="font-bold">•</span>
    </div>
    <div className="text-3xl font-extrabold" style={{ color }}>{value}</div>
    <div className="text-slate-500 text-sm mt-1">{label}</div>
  </button>
);

const QuickActionCard = ({ Icon, label, color, bg, onClick, testid }) => (
  <button
    type="button"
    onClick={onClick}
    className="sns-card p-3 flex flex-col items-center gap-2 text-center hover:shadow-md hover:-translate-y-0.5 active:scale-[0.95] transition-all cursor-pointer w-full"
    data-testid={testid}
  >
    <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: bg, color }}>
      <Icon size={20} strokeWidth={2.3} />
    </div>
    <div className="text-[11px] font-bold text-slate-700 leading-tight">{label}</div>
  </button>
);

const localISO = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const MiniJobCard = ({ m, emb }) => {
  const t = (iso) => iso ? new Date(iso).toTimeString().slice(0, 5) : "—";
  const pausedH = (m.paused_seconds || 0) / 3600;
  return (
    <Link to={`/trabajos/${m.id}`} className="sns-card p-4 flex items-center gap-4 hover:shadow-md transition-shadow" data-testid={`mini-job-${m.id}`}>
      <div className="w-14 h-14 rounded-xl bg-[var(--ink)] text-white flex items-center justify-center font-bold shrink-0">
        <Clock size={22} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-bold truncate" data-testid={`mini-job-name-${m.id}`}>{emb?.name || "—"}</div>
        <div className="text-slate-500 text-sm">
          {m.end_at ? `${t(m.start_at)} · ${t(m.end_at)}` : t(m.start_at)}
          {m.status === "finalizado" && m.hours_worked != null && (
            <> · <span className="font-semibold text-emerald-700">{fmtHours(m.hours_worked)}</span></>
          )}
          {pausedH > 0 && (
            <> · <span className="text-amber-700 inline-flex items-center gap-0.5"><Pause size={10} />{fmtHours(pausedH)}</span></>
          )}
        </div>
      </div>
      <StatusPill status={m.status} />
    </Link>
  );
};

const DOT = { programado: "bg-[#2D62B7]", en_curso: "bg-amber-500", pausado: "bg-amber-500", retraso: "bg-red-500", finalizado: "bg-emerald-600" };

const CalendarCell = ({ cell, onSelect }) => {
  if (!cell) return <div />;
  const hasWork = !!cell.info;
  const hasJobs = cell.jobs.length > 0;
  return (
    <button
      onClick={onSelect}
      disabled={!hasWork && !hasJobs}
      className={`aspect-square rounded-xl flex flex-col items-center justify-center text-xs transition-all ${
        hasWork
          ? "bg-[var(--coral)]/10 border border-[var(--coral)]/30 hover:bg-[var(--coral)]/20 cursor-pointer"
          : hasJobs ? "bg-[var(--sky)] hover:bg-[#DCE9FF] cursor-pointer" : "hover:bg-slate-50"
      }`}
      data-testid={`cal-day-${cell.iso}`}
    >
      <div className={`font-semibold ${hasWork ? "text-[var(--coral)]" : "text-slate-600"}`}>{cell.day}</div>
      {hasWork && (
        <div className="text-[9px] font-bold text-[var(--coral)] leading-none mt-0.5">{fmtHours(cell.info.hours)}</div>
      )}
      {hasJobs && (
        <div className="flex gap-0.5 mt-1" data-testid={`cal-dots-${cell.iso}`}>
          {cell.jobs.slice(0, 4).map((j) => <span key={j.id} className={`w-1.5 h-1.5 rounded-full ${DOT[j.status] || "bg-slate-400"}`} />)}
        </div>
      )}
    </button>
  );
};

const DayDetailModal = ({ day, jobs, embMap, onClose, t, lang }) => {
  const d = new Date(day.date + "T00:00:00");
  const months = lang === "en" ? MONTHS_EN : MONTHS_ES;
  const dayStr = `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
  const fmtReal = (iso) => iso ? new Date(iso).toTimeString().slice(0, 5) : "—";
  // Nota: iteramos sobre day.items para mostrar la info real por servicio
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-end md:items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-3xl w-full max-w-md p-5 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="day-detail-modal">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-400">{t("inicio.day")}</div>
            <h3 className="sns-title text-2xl">{dayStr}</h3>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-full hover:bg-slate-100 flex items-center justify-center shrink-0">
            <X size={18} />
          </button>
        </div>

        {jobs.length > 0 && (
          <div className="space-y-2 mb-4" data-testid="day-jobs-list">
            <div className="text-xs uppercase tracking-wider font-bold text-[var(--sky-text)]">{t("nav.trabajos", "Mantenimientos")} ({jobs.length})</div>
            {jobs.map((j) => <MiniJobCard key={j.id} m={j} emb={embMap[j.embarcacion_id]} />)}
          </div>
        )}
        <div className="space-y-3">
          {(day.items || []).map((it) => {
            const emb = embMap[it.embarcacion_id];
            return (
              <Link
                to={`/trabajos/${it.id}`}
                key={it.id}
                className="block p-4 rounded-2xl border border-[var(--border)] hover:bg-slate-50 transition-colors"
                data-testid={`day-item-${it.id}`}
              >
                <div className="font-bold truncate mb-2">{emb?.name || "—"}</div>
                <div className="grid grid-cols-2 gap-y-1 gap-x-3 text-sm">
                  <div className="text-slate-500">{t("inicio.start")}</div>
                  <div className="text-right font-semibold tabular-nums">{fmtReal(it.checkin_at || it.start_at)}</div>
                  <div className="text-slate-500">{t("inicio.end")}</div>
                  <div className="text-right font-semibold tabular-nums">{fmtReal(it.checkout_at || it.end_at)}</div>
                  <div className="text-slate-500 flex items-center gap-1"><Pause size={12} /> {t("inicio.pause")}</div>
                  <div className="text-right font-semibold tabular-nums text-amber-700">{fmtHours(it.paused_hours || 0)}</div>
                  <div className="text-slate-500 flex items-center gap-1"><Clock size={12} /> {t("inicio.worked")}</div>
                  <div className="text-right font-bold tabular-nums text-emerald-700">{fmtHours(it.hours || 0)}</div>
                </div>
              </Link>
            );
          })}
        </div>

        {/* Totales al final del listado */}
        <div className="mt-5 pt-4 border-t-2 border-[var(--border)] bg-slate-50 -mx-5 -mb-5 px-5 pb-5 rounded-b-3xl">
          <div className="grid grid-cols-2 gap-y-2 gap-x-3 text-sm">
            <div className="text-slate-500">{t("inicio.day_total_pause")}</div>
            <div className="text-right font-semibold tabular-nums text-amber-700">{fmtHours(day.paused_hours || 0)}</div>
            <div className="text-slate-700 font-bold">{t("inicio.day_total_worked")}</div>
            <div className="text-right font-extrabold tabular-nums text-[var(--coral)] text-lg" data-testid="day-total-hours">{fmtHours(day.hours || 0)}</div>
          </div>
        </div>
      </div>
    </div>
  );
};
